/**
 * BootErrorBoundary.jsx
 *
 * Catches render errors and shows the current boot stage + recent history
 * so support/dev can see exactly where loading failed.
 */

import React from 'react';
import {
  formatBootDiagnostics,
  getBootStage,
  getBootHistory,
} from '../lib/bootStage.js';

export default class BootErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null, componentStack: null };
  }

  static getDerivedStateFromError(error) {
    return { error };
  }

  componentDidCatch(error, info) {
    const componentStack = info?.componentStack || null;
    this.setState({ componentStack });

    const diag = formatBootDiagnostics({
      error,
      componentStack,
      stageId: this.props.stageId,
      stageLabel: this.props.stageLabel,
    });
    console.error('[BootErrorBoundary]', diag);
  }

  render() {
    const { error, componentStack } = this.state;
    if (!error) return this.props.children;

    const stage = getBootStage();
    const history = getBootHistory();
    const boundaryStage = this.props.stageId
      ? { id: this.props.stageId, label: this.props.stageLabel || this.props.stageId }
      : stage;

    return (
      <div className="boot-error-page">
        <div className="boot-error-card">
          <strong className="boot-error-title">
            Something went wrong loading AE Tracker
          </strong>

          <div className="boot-error-section">
            <div className="boot-error-kicker">Where it failed</div>
            <div className="boot-error-stage">
              <code>{boundaryStage.id}</code>
              <span>{boundaryStage.label}</span>
            </div>
            {stage.detail && (
              <div className="boot-error-detail">{stage.detail}</div>
            )}
          </div>

          <div className="boot-error-section">
            <div className="boot-error-kicker">Error</div>
            <pre className="boot-error-message">{error.message || String(error)}</pre>
          </div>

          {history.length > 1 && (
            <div className="boot-error-section">
              <div className="boot-error-kicker">Recent stages</div>
              <ol className="boot-error-trail">
                {history.slice(-6).map((s, i) => (
                  <li key={`${s.id}-${s.at}-${i}`}>
                    <code>{s.id}</code>
                    <span>{s.label}</span>
                    {s.detail && <em>{s.detail}</em>}
                  </li>
                ))}
              </ol>
            </div>
          )}

          {componentStack && (
            <details className="boot-error-stack">
              <summary>Component stack</summary>
              <pre>{componentStack.trim()}</pre>
            </details>
          )}

          <button
            type="button"
            className="boot-error-reload"
            onClick={() => window.location.reload()}
          >
            Reload page
          </button>
        </div>
      </div>
    );
  }
}
